"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import { ArrowUpRight, CalendarCheck, FolderDown, Gift, Image as ImageIcon, MonitorPlay, QrCode, Smartphone, Users } from "lucide-react";
import BuyEventButton from "../buyEventButton";
import { Link } from "@/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useCurrencyPrices } from "@/hooks/useCurrencyPrices";
import { DetectedLocalPricingNudge } from "@/components/index/LocalPricingNudge";
import { cn } from "@/lib/utils";
import { formatOfferPrice, type PlanPackageType, type PublicOffer } from "@/lib/offers";
interface PricingCardsProps {
  offerPlacement?: string;
  offers?: Partial<Record<PlanPackageType, PublicOffer>>;
  thankYouCardPromotion?: ThankYouCardPromotionDetails;
}
interface ThankYouCardPromotionDetails {
  quantity: number;
  offerPlacement: string;
  badge: string;
}

/** Literal class lists per page theme, so Tailwind sees every colour. */
interface PricingTheme {
  basicCard: string;
  premiumCard: string;
  basicMuted: string;
  premiumMuted: string;
  basicRule: string;
  premiumRule: string;
  basicBarOn: string;
  basicBarOff: string;
  premiumBarOn: string;
  premiumBarOff: string;
  badge: string;
  basicButton: string;
  premiumButton: string;
  arrow: string;
  sharedIcon: string;
  sharedText: string;
  sharedBorder: string;
  footnote: string;
  /** The landing page's wedding photos; only where weddings fit the page. */
  hasPhotos: boolean;
  hasLocalPricingNudge: boolean;
}
const PREMIUM_SHADOW = "shadow-[0_30px_60px_-34px_rgba(30,30,40,0.95)]";
const BUTTON_SHADOW = "shadow-[0_14px_30px_-16px_rgba(30,30,40,0.7)]";
const PRICING_THEMES = {
  default: {
    basicCard: "bg-gg-orange-100 text-dark",
    premiumCard: `bg-dark text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-dark/75",
    premiumMuted: "text-white/75",
    basicRule: "border-dark/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-gg-orange-600",
    basicBarOff: "bg-dark/10",
    premiumBarOn: "bg-primary",
    premiumBarOff: "bg-white/15",
    badge: "bg-gg-orange-300 text-dark",
    basicButton: `bg-dark text-white hover:bg-[#43444f] ${BUTTON_SHADOW}`,
    premiumButton: `bg-gg-orange-100 text-dark hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-primary text-dark",
    sharedIcon: "bg-gg-orange-100 text-[#9A5A00]",
    sharedText: "text-dark",
    sharedBorder: "border-dark/10",
    footnote: "text-dark/70",
    hasPhotos: true,
    hasLocalPricingNudge: true
  },
  christmas: {
    basicCard: "bg-white text-dark ring-1 ring-dark/10",
    premiumCard: `bg-dark text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-dark/75",
    premiumMuted: "text-white/75",
    basicRule: "border-dark/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-gg-christmas-red",
    basicBarOff: "bg-dark/10",
    premiumBarOn: "bg-[#E57373]",
    premiumBarOff: "bg-white/15",
    badge: "bg-gg-christmas-red text-white",
    basicButton: `bg-dark text-white hover:bg-[#43444f] ${BUTTON_SHADOW}`,
    premiumButton: `bg-white text-dark hover:bg-white/85 ${BUTTON_SHADOW}`,
    arrow: "bg-gg-christmas-red text-white",
    sharedIcon: "bg-[#FBEAEA] text-gg-christmas-red",
    sharedText: "text-dark",
    sharedBorder: "border-dark/10",
    footnote: "text-dark/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  konfirmation: {
    basicCard: "bg-[#F5EDE8] text-[#3A2C24]",
    premiumCard: `bg-[#3A2C24] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#3A2C24]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#3A2C24]/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-[#B08E7E]",
    basicBarOff: "bg-[#3A2C24]/10",
    premiumBarOn: "bg-[#D9B8A8]",
    premiumBarOff: "bg-white/15",
    badge: "bg-[#D9B8A8] text-[#3A2C24]",
    basicButton: `bg-[#3A2C24] text-white hover:bg-[#4A3A30] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#F5EDE8] text-[#3A2C24] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#B08E7E] text-white",
    sharedIcon: "bg-[#F5EDE8] text-[#7D5F50]",
    sharedText: "text-[#3A2C24]",
    sharedBorder: "border-[#3A2C24]/10",
    footnote: "text-[#3A2C24]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  barnedab: {
    basicCard: "bg-[#F0EBF8] text-[#2D1D42]",
    premiumCard: `bg-[#3D2858] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#2D1D42]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#2D1D42]/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-[#8B72A8]",
    basicBarOff: "bg-[#2D1D42]/10",
    premiumBarOn: "bg-[#C9B6E4]",
    premiumBarOff: "bg-white/15",
    badge: "bg-[#C9B6E4] text-[#2D1D42]",
    basicButton: `bg-[#3D2858] text-white hover:bg-[#4B3369] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#F0EBF8] text-[#2D1D42] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#8B72A8] text-white",
    sharedIcon: "bg-[#F0EBF8] text-[#5C4278]",
    sharedText: "text-[#2D1D42]",
    sharedBorder: "border-[#2D1D42]/10",
    footnote: "text-[#2D1D42]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  fodselsdag: {
    basicCard: "bg-[#FBF5E6] text-[#3A2A08]",
    premiumCard: `bg-[#3A2A08] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#3A2A08]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#3A2A08]/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-[#B08A2A]",
    basicBarOff: "bg-[#3A2A08]/10",
    premiumBarOn: "bg-[#E5C66B]",
    premiumBarOff: "bg-white/15",
    badge: "bg-[#E5C66B] text-[#3A2A08]",
    basicButton: `bg-[#3A2A08] text-white hover:bg-[#4A370F] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#FBF5E6] text-[#3A2A08] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#8C6D1C] text-white",
    sharedIcon: "bg-[#FBF5E6] text-[#6B5016]",
    sharedText: "text-[#3A2A08]",
    sharedBorder: "border-[#3A2A08]/10",
    footnote: "text-[#3A2A08]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  },
  barMitsva: {
    basicCard: "bg-[#E8EEF8] text-[#1A2A50]",
    premiumCard: `bg-[#1A2A50] text-white ${PREMIUM_SHADOW}`,
    basicMuted: "text-[#1A2A50]/75",
    premiumMuted: "text-white/75",
    basicRule: "border-[#1A2A50]/10",
    premiumRule: "border-white/15",
    basicBarOn: "bg-[#2C4A8C]",
    basicBarOff: "bg-[#1A2A50]/10",
    premiumBarOn: "bg-[#9DB5E6]",
    premiumBarOff: "bg-white/15",
    badge: "bg-[#9DB5E6] text-[#1A2A50]",
    basicButton: `bg-[#1A2A50] text-white hover:bg-[#243766] ${BUTTON_SHADOW}`,
    premiumButton: `bg-[#E8EEF8] text-[#1A2A50] hover:bg-white ${BUTTON_SHADOW}`,
    arrow: "bg-[#2C4A8C] text-white",
    sharedIcon: "bg-[#E8EEF8] text-[#2C4A8C]",
    sharedText: "text-[#1A2A50]",
    sharedBorder: "border-[#1A2A50]/10",
    footnote: "text-[#1A2A50]/70",
    hasPhotos: false,
    hasLocalPricingNudge: false
  }
} as const satisfies Record<string, PricingTheme>;
type PricingThemeName = keyof typeof PRICING_THEMES;
const PHOTO_BASE_URL = "https://kycacontent.s3.eu-central-1.amazonaws.com/guestgallery/landing-page";

// Two polaroids peek over Premium's corner and fan out on hover, each with its
// own tilt, travel and timing.
const PEEK_PHOTOS = [{
  src: "3",
  className: "right-16 -rotate-[9deg] duration-[620ms] can-hover:group-hover/premium:-translate-x-3 can-hover:group-hover/premium:-translate-y-1 can-hover:group-hover/premium:-rotate-[14deg] md:right-20"
}, {
  src: "6",
  className: "right-4 rotate-[7deg] duration-500 can-hover:group-hover/premium:-translate-y-2 can-hover:group-hover/premium:rotate-[11deg] md:right-6"
}] as const;
const MONTHS = Array.from({
  length: 12
}, (_, index) => index);
function OfferCallout(t0) {
  const $ = _c(16);
  const {
    offer,
    isDark
  } = t0;
  if (!offer) {
    return null;
  }
  let t1;
  if ($[0] !== isDark || $[1] !== offer.badge || $[2] !== offer.benefits || $[3] !== offer.description || $[4] !== offer.headline) {
    const benefitLabels = offer.benefits.map(_temp).filter(_temp2);
    const t2 = isDark ? "bg-white/[0.08]" : "bg-white/70 ring-1 ring-black/5";
    let t3;
    if ($[6] !== t2) {
      t3 = cn("mt-5 rounded-xl px-4 py-3 text-sm", t2);
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== isDark || $[9] !== offer.badge) {
      t4 = offer.badge ? <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", isDark ? "bg-gg-orange-300 text-dark" : "bg-gg-orange-300/60 text-[#5c3d0a]")}>{offer.badge}</span> : null;
      $[8] = isDark;
      $[9] = offer.badge;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== offer.headline || $[12] !== t4) {
      t5 = <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold leading-5">{t4}{offer.headline}</p>;
      $[11] = offer.headline;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== offer.description) {
      t6 = offer.description ? <p className="mt-1 text-xs leading-5 opacity-75">{offer.description}</p> : null;
      $[14] = offer.description;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t1 = <div className={t3}>{t5}{t6}{benefitLabels.length > 0 ? <p className="mt-1 text-xs font-medium">{benefitLabels.join(" \xB7 ")}</p> : null}</div>;
    $[0] = isDark;
    $[1] = offer.badge;
    $[2] = offer.benefits;
    $[3] = offer.description;
    $[4] = offer.headline;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp2(label) {
  return Boolean(label);
}
function _temp(benefit) {
  return benefit.label;
}
function ThankYouCardsPromotion(t0) {
  const $ = _c(10);
  const {
    promotion
  } = t0;
  const locale = useLocale();
  if (locale !== "da" || !promotion) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#f3dfae] text-[#a64e39]"><Gift className="h-4 w-4" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="whitespace-nowrap font-normal text-[#655d52]">(Værdi 240 kr.)</span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== promotion.quantity) {
    t3 = <p className="font-semibold leading-5">{promotion.quantity} gratis takkekort inkluderet{" "}{t2}</p>;
    $[2] = promotion.quantity;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Link href="/printed-for-you" className="inline-flex items-center gap-0.5 font-semibold text-[#2f4f49] underline decoration-[#2f4f49]/30 underline-offset-4 hover:text-[#a64e39]">Læs mere<ArrowUpRight aria-hidden="true" className="h-3 w-3" /></Link>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== promotion.badge) {
    t5 = <p className="mt-0.5 text-xs leading-5 text-[#655d52]">{promotion.badge} · Med et billede efter eget valg og en QR-kode direkte til jeres galleri. Fragt 49 kr. · Ekstra takkekort: 12 kr. pr. stk.{" "}{t4}</p>;
    $[5] = promotion.badge;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <aside className="mb-1 flex gap-3 rounded-xl bg-[#fff8e5] p-3.5 text-sm text-[#29251f]">{t1}<div className="min-w-0 flex-1">{t3}{t5}</div></aside>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function MonthBar(t0) {
  const $ = _c(23);
  const {
    months,
    on,
    off,
    muted
  } = t0;
  const t = useTranslations("Pricing");
  let t1;
  if ($[0] !== months || $[1] !== off || $[2] !== on) {
    t1 = MONTHS.map(month => <span key={month} className={cn("h-2 flex-1 rounded-full", month < months ? on : off)} />);
    $[0] = months;
    $[1] = off;
    $[2] = on;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div className="flex gap-1">{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== muted) {
    t3 = cn("flex justify-between text-xs", muted);
    $[6] = muted;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("timelineStart");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <span>{t4}</span>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("timelineEnd");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <span>{t6}</span>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t7) {
    t8 = <div className={t3}>{t5}{t7}</div>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t2 || $[21] !== t8) {
    t9 = <div aria-hidden="true" className="flex flex-col gap-1.5">{t2}{t8}</div>;
    $[20] = t2;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
function PlanCard(t0) {
  const $ = _c(139);
  const {
    packageType,
    theme,
    offer,
    offerPlacement,
    thankYouCardPromotion
  } = t0;
  const isPremium = packageType === "premium";
  const t = useTranslations(isPremium ? "Pricing.Premium" : "Pricing.Basic");
  const tPricing = useTranslations("Pricing");
  const currencyPrices = useCurrencyPrices();
  const locale = useLocale();
  let t1;
  if ($[0] !== locale || $[1] !== offer) {
    t1 = offer ? formatOfferPrice(offer, locale) : null;
    $[0] = locale;
    $[1] = offer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const offerPrice = t1;
  let t2;
  if ($[3] !== currencyPrices?.basicPrice || $[4] !== currencyPrices?.premiumPrice || $[5] !== isPremium || $[6] !== offerPrice || $[7] !== t) {
    t2 = offerPrice ?? (isPremium ? currencyPrices?.premiumPrice : currencyPrices?.basicPrice) ?? t("price");
    $[3] = currencyPrices?.basicPrice;
    $[4] = currencyPrices?.premiumPrice;
    $[5] = isPremium;
    $[6] = offerPrice;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const price = t2;
  let t3;
  if ($[9] !== isPremium || $[10] !== t) {
    t3 = isPremium ? t("PricingCard.title") : "Basic";
    $[9] = isPremium;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const name = t3;
  const muted = isPremium ? theme.premiumMuted : theme.basicMuted;
  let t4;
  if ($[12] !== currencyPrices?.rebateAmount || $[13] !== isPremium || $[14] !== offerPrice || $[15] !== t || $[16] !== tPricing) {
    t4 = isPremium && !offerPrice ? tPricing("savingVsUpgrade", {
      amount: currencyPrices?.rebateAmount ?? t("PricingCard.rebateAmount")
    }) : tPricing("secureCheckout");
    $[12] = currencyPrices?.rebateAmount;
    $[13] = isPremium;
    $[14] = offerPrice;
    $[15] = t;
    $[16] = tPricing;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const footnote = t4;
  let t5;
  if ($[18] !== isPremium || $[19] !== theme.basicCard || $[20] !== theme.premiumCard) {
    t5 = isPremium ? cn("group/premium", theme.premiumCard) : theme.basicCard;
    $[18] = isPremium;
    $[19] = theme.basicCard;
    $[20] = theme.premiumCard;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t5) {
    t6 = cn("relative flex flex-col rounded-[1.75rem] p-6 md:p-8", t5);
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== isPremium || $[25] !== theme.hasPhotos) {
    t7 = isPremium && theme.hasPhotos ? PEEK_PHOTOS.map(_temp3) : null;
    $[24] = isPremium;
    $[25] = theme.hasPhotos;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== name) {
    t8 = <h3 className="text-lg font-semibold">{name}</h3>;
    $[27] = name;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== isPremium || $[30] !== tPricing || $[31] !== theme.badge) {
    t9 = isPremium ? <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", theme.badge)}>{tPricing("recommended")}</span> : null;
    $[29] = isPremium;
    $[30] = tPricing;
    $[31] = theme.badge;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t8 || $[34] !== t9) {
    t10 = <div className="flex items-center gap-2">{t8}{t9}</div>;
    $[33] = t8;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== price) {
    t11 = <span className="text-5xl font-bold tracking-[-0.03em] tabular-nums">{price}</span>;
    $[36] = price;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t) {
    t12 = t("PricingCard.pricePostfix");
    $[38] = t;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== muted || $[41] !== t12) {
    t13 = <span className={muted}>{t12}</span>;
    $[40] = muted;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t11 || $[44] !== t13) {
    t14 = <p className="mt-2 flex flex-wrap items-baseline gap-x-2">{t11}{t13}</p>;
    $[43] = t11;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== muted) {
    t15 = cn("text-sm", muted);
    $[46] = muted;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== tPricing) {
    t16 = tPricing("oneTimePayment");
    $[48] = tPricing;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t15 || $[51] !== t16) {
    t17 = <p className={t15}>{t16}</p>;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t10 || $[54] !== t14 || $[55] !== t17) {
    t18 = <header className="flex flex-col gap-1">{t10}{t14}{t17}</header>;
    $[53] = t10;
    $[54] = t14;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== isPremium || $[58] !== offer) {
    t19 = <OfferCallout offer={offer} isDark={isPremium} />;
    $[57] = isPremium;
    $[58] = offer;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  const t20 = isPremium ? theme.premiumRule : theme.basicRule;
  let t21;
  if ($[60] !== t20) {
    t21 = cn("mt-6 flex flex-col gap-4 border-t pt-6", t20);
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== muted) {
    t22 = cn("text-sm", muted);
    $[62] = muted;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== tPricing) {
    t23 = tPricing("uploadsLabel");
    $[64] = tPricing;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t22 || $[67] !== t23) {
    t24 = <dt className={t22}>{t23}</dt>;
    $[66] = t22;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t) {
    t25 = t("storageShort");
    $[69] = t;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t25) {
    t26 = <dd className="text-2xl font-bold tracking-[-0.02em]">{t25}</dd>;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  let t27;
  if ($[73] !== t24 || $[74] !== t26) {
    t27 = <div>{t24}{t26}</div>;
    $[73] = t24;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== muted) {
    t28 = cn("text-sm", muted);
    $[76] = muted;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== tPricing) {
    t29 = tPricing("galleryOpenLabel");
    $[78] = tPricing;
    $[79] = t29;
  } else {
    t29 = $[79];
  }
  let t30;
  if ($[80] !== t28 || $[81] !== t29) {
    t30 = <dt className={t28}>{t29}</dt>;
    $[80] = t28;
    $[81] = t29;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== t) {
    t31 = t("windowShort");
    $[83] = t;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== t31) {
    t32 = <dd className="text-2xl font-bold tracking-[-0.02em]">{t31}</dd>;
    $[85] = t31;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] !== t30 || $[88] !== t32) {
    t33 = <div>{t30}{t32}</div>;
    $[87] = t30;
    $[88] = t32;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== t27 || $[91] !== t33) {
    t34 = <dl className="grid grid-cols-2 gap-4">{t27}{t33}</dl>;
    $[90] = t27;
    $[91] = t33;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  const t35 = isPremium ? 12 : 3;
  const t36 = isPremium ? theme.premiumBarOn : theme.basicBarOn;
  const t37 = isPremium ? theme.premiumBarOff : theme.basicBarOff;
  let t38;
  if ($[93] !== muted || $[94] !== t35 || $[95] !== t36 || $[96] !== t37) {
    t38 = <MonthBar months={t35} on={t36} off={t37} muted={muted} />;
    $[93] = muted;
    $[94] = t35;
    $[95] = t36;
    $[96] = t37;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== muted) {
    t39 = cn("text-sm leading-5", muted);
    $[98] = muted;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  let t40;
  if ($[100] !== t) {
    t40 = t("PricingCard.EventBenefits.imagesAndVideos");
    $[100] = t;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t39 || $[103] !== t40) {
    t41 = <p className={t39}>{t40}</p>;
    $[102] = t39;
    $[103] = t40;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== t21 || $[106] !== t34 || $[107] !== t38 || $[108] !== t41) {
    t42 = <div className={t21}>{t34}{t38}{t41}</div>;
    $[105] = t21;
    $[106] = t34;
    $[107] = t38;
    $[108] = t41;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  let t43;
  if ($[110] !== isPremium || $[111] !== thankYouCardPromotion) {
    t43 = isPremium ? <ThankYouCardsPromotion promotion={thankYouCardPromotion} /> : null;
    $[110] = isPremium;
    $[111] = thankYouCardPromotion;
    $[112] = t43;
  } else {
    t43 = $[112];
  }
  const t44 = isPremium ? theme.premiumButton : theme.basicButton;
  const t45 = theme.arrow;
  let t46;
  if ($[113] !== name || $[114] !== tPricing) {
    t46 = tPricing("choosePlan", {
      plan: name
    });
    $[113] = name;
    $[114] = tPricing;
    $[115] = t46;
  } else {
    t46 = $[115];
  }
  const t47 = isPremium ? thankYouCardPromotion?.offerPlacement ?? offerPlacement : offerPlacement;
  let t48;
  if ($[116] !== offer || $[117] !== packageType || $[118] !== t44 || $[119] !== t46 || $[120] !== t47 || $[121] !== theme.arrow) {
    t48 = <BuyEventButton appearance="pill" variant="dark" className={t44} arrowClassName={t45} label={t46} packageType={packageType} offerPlacement={t47} offer={offer} />;
    $[116] = offer;
    $[117] = packageType;
    $[118] = t44;
    $[119] = t46;
    $[120] = t47;
    $[121] = theme.arrow;
    $[122] = t48;
  } else {
    t48 = $[122];
  }
  let t49;
  if ($[123] !== muted) {
    t49 = cn("text-center text-xs", muted);
    $[123] = muted;
    $[124] = t49;
  } else {
    t49 = $[124];
  }
  let t50;
  if ($[125] !== footnote || $[126] !== t49) {
    t50 = <p className={t49}>{footnote}</p>;
    $[125] = footnote;
    $[126] = t49;
    $[127] = t50;
  } else {
    t50 = $[127];
  }
  let t51;
  if ($[128] !== t43 || $[129] !== t48 || $[130] !== t50) {
    t51 = <div className="mt-auto flex flex-col gap-3 pt-7">{t43}{t48}{t50}</div>;
    $[128] = t43;
    $[129] = t48;
    $[130] = t50;
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  let t52;
  if ($[132] !== t18 || $[133] !== t19 || $[134] !== t42 || $[135] !== t51 || $[136] !== t6 || $[137] !== t7) {
    t52 = <article className={t6}>{t7}{t18}{t19}{t42}{t51}</article>;
    $[132] = t18;
    $[133] = t19;
    $[134] = t42;
    $[135] = t51;
    $[136] = t6;
    $[137] = t7;
    $[138] = t52;
  } else {
    t52 = $[138];
  }
  return t52;
}
function _temp3(photo) {
  return <div key={photo.src} aria-hidden="true" className={cn("absolute -top-9 w-14 rounded-[4px] bg-white p-1 pb-3.5 shadow-[0_12px_24px_-12px_rgba(0,0,0,0.7)] transition-transform ease-[var(--ease-overshoot)] motion-reduce:transition-none md:-top-11 md:w-[4.5rem]", photo.className)}><div className="relative aspect-[4/5] overflow-hidden rounded-[2px] bg-stone-200"><Image alt="" className="object-cover" fill={true} sizes="72px" src={`${PHOTO_BASE_URL}/${photo.src}.webp`} /></div></div>;
}
const SHARED_FEATURES = [{
  icon: ImageIcon,
  key: "Basic.PricingCard.EventBenefits.originalQuality"
}, {
  icon: Users,
  key: "Basic.PricingCard.EventBenefits.downloadable"
}, {
  icon: Smartphone,
  key: "Included.noApp"
}, {
  icon: FolderDown,
  key: "Included.zip"
}, {
  icon: MonitorPlay,
  key: "Included.photowall"
}, {
  icon: QrCode,
  key: "Included.qrCards"
}, {
  icon: CalendarCheck,
  key: "chooseDateNote"
}] as const;

/**
 * Basic and Premium side by side: price, the two facts that differ (storage
 * and months open), then everything both plans share, listed once.
 */
function PricingPlans(t0) {
  const $ = _c(50);
  const {
    themeName,
    offerPlacement,
    offers,
    thankYouCardPromotion
  } = t0;
  const t = useTranslations("Pricing");
  const theme = PRICING_THEMES[themeName];
  let t1;
  if ($[0] !== theme.hasLocalPricingNudge) {
    t1 = theme.hasLocalPricingNudge ? <DetectedLocalPricingNudge className="mb-1 self-center" /> : null;
    $[0] = theme.hasLocalPricingNudge;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = theme.hasPhotos ? "gap-y-14 md:gap-y-4 md:pt-10" : "gap-y-4";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("grid grid-cols-1 gap-x-4 md:grid-cols-2", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = offers?.basic;
  let t5;
  if ($[4] !== offerPlacement || $[5] !== t4 || $[6] !== theme) {
    t5 = <PlanCard packageType="basic" theme={theme} offer={t4} offerPlacement={offerPlacement} />;
    $[4] = offerPlacement;
    $[5] = t4;
    $[6] = theme;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = offers?.premium;
  let t7;
  if ($[8] !== offerPlacement || $[9] !== t6 || $[10] !== thankYouCardPromotion || $[11] !== theme) {
    t7 = <PlanCard packageType="premium" theme={theme} offer={t6} offerPlacement={offerPlacement} thankYouCardPromotion={thankYouCardPromotion} />;
    $[8] = offerPlacement;
    $[9] = t6;
    $[10] = thankYouCardPromotion;
    $[11] = theme;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t5 || $[15] !== t7) {
    t8 = <div className={t3}>{t5}{t7}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== theme.sharedBorder || $[18] !== theme.sharedText) {
    t9 = cn("rounded-[1.75rem] border px-6 py-6 md:px-8", theme.sharedBorder, theme.sharedText);
    $[17] = theme.sharedBorder;
    $[18] = theme.sharedText;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("bothPlansInclude");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <h3 className="text-base font-semibold">{t10}</h3>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t || $[25] !== theme.sharedIcon) {
    t12 = SHARED_FEATURES.map(t13 => {
      const {
        icon: Icon,
        key
      } = t13;
      return <li key={key} className="flex items-start gap-2.5"><span aria-hidden="true" className={cn("grid h-7 w-7 shrink-0 place-items-center rounded-lg", theme.sharedIcon)}><Icon className="h-3.5 w-3.5" /></span><span className="pt-1 leading-5">{t(key)}</span></li>;
    });
    $[24] = t;
    $[25] = theme.sharedIcon;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">{t12}</ul>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t11 || $[30] !== t13 || $[31] !== t9) {
    t14 = <section className={t9}>{t11}{t13}</section>;
    $[29] = t11;
    $[30] = t13;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== theme.footnote) {
    t15 = cn("text-xs leading-5", theme.footnote);
    $[33] = theme.footnote;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("fairUsePolicy");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("fairUseLink");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17) {
    t18 = <Link href="/terms-and-conditions" className="underline underline-offset-2" target="_blank">{t17}</Link>;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t15 || $[42] !== t16 || $[43] !== t18) {
    t19 = <p className={t15}>*{t16}{" "}{t18}.</p>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t1 || $[46] !== t14 || $[47] !== t19 || $[48] !== t8) {
    t20 = <div className="flex flex-col gap-4">{t1}{t8}{t14}{t19}</div>;
    $[45] = t1;
    $[46] = t14;
    $[47] = t19;
    $[48] = t8;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
}
export function PricingCards(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="default" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsChristmas(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="christmas" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsKonfirmation(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="konfirmation" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsBarnedab(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="barnedab" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsFodselsdag(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="fodselsdag" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function PricingCardsBarMitsva(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const props = t1;
  let t2;
  if ($[2] !== props) {
    t2 = <PricingPlans themeName="barMitsva" {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}